import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authAPI } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Check if there's a token in localStorage
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return null;
    }
    
    try {
      const res = await authAPI.getMe();
      setUser(res.data);
      return res.data;
    } catch {
      setUser(null);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    // Always check auth if there's a token, even on public pages
    // This allows auto-redirect to dashboard if already logged in
    const token = localStorage.getItem('auth_token');
    if (token) {
      checkAuth().then(userData => {
        // If user is valid and on login page, redirect to dashboard
        if (userData && window.location.pathname === '/login') {
          window.location.href = '/dashboard';
        }
      });
      return;
    }
    
    // No token - just set loading to false on public pages
    const publicPaths = ['/login', '/forgot-password', '/reset-password', '/verify-otp', '/privacy', '/redeem', '/subscription', '/invite', '/delete-account'];
    const isPublicPath = publicPaths.some(p => window.location.pathname.startsWith(p));
    if (isPublicPath) {
      setLoading(false);
      return;
    }
    
    // Not on public page and no token - will redirect via api interceptor
    setLoading(false);
  }, [checkAuth]);

  const login = (userData, token) => {
    setUser(userData);
    if (token) localStorage.setItem('auth_token', token);
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch { /* ignore */ }
    setUser(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
